import { useMemo } from 'react'
import { collection, orderBy, query } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { COLLECTIONS, computeBookletStatus, type Booklet } from '@shared/model'
import { useQuerySnapshot } from './useQuerySnapshot'

export interface BookletsResult {
  booklets: Booklet[]
  /** Den period som pågår just nu. Normalt finns bara en. */
  activeBooklet: Booklet | null
  loading: boolean
  error: Error | null
}

/**
 * Alla perioder, nyast först.
 *
 * Statusen räknas om lokalt utifrån datumen i stället för att lita på fältet
 * i databasen. Den schemalagda funktionen som skriver status kör en gång per
 * dygn, och ett häfte ska bli aktivt vid midnatt – inte när servern råkar
 * vakna.
 */
export function useBooklets(): BookletsResult {
  const bookletsQuery = useMemo(
    () => query(collection(db, COLLECTIONS.booklets), orderBy('startDate', 'desc')),
    [],
  )

  const { data, loading, error } = useQuerySnapshot<Booklet>(bookletsQuery)

  const booklets = useMemo(
    () => data.map((booklet) => ({ ...booklet, status: computeBookletStatus(booklet) })),
    [data],
  )

  const activeBooklet = useMemo(
    () => booklets.find((booklet) => booklet.status === 'active' && !booklet.isTest) ?? null,
    [booklets],
  )

  return { booklets, activeBooklet, loading, error }
}
